/**
 * Copyright 2020 New Light Technologies, Inc.
 *
 * With Supporting Sponsorship from the Federal Emergency Management Agency (Contract: GSA Stars II GS-06F-0968Z)
 * In accordance with FAR 52.227-14(c)(iii), New Light Technologies, Inc. grants to the Government and others acting on its behalf a paid-up, nonexclusive, irrevocable, worldwide license in such copyrighted computer software and data to reproduce, prepare derivative works, and perform publicly and display publicly (but not to distribute copies to the public) by or on behalf of the Government.
 * Any other use, distribution, reproduction, modification, or publication without the prior express written authorization of New Light Technologies, Inc. is strictly prohibited.
 * All other rights are reserved by their respective copyright holders.
 *
 */
import { Button, Menu, MenuItem, Tooltip } from '@mui/material';
import { currentRegionState } from './regionsSelector';
import { Language } from '@mui/icons-material';
import { atom } from 'jotai';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { loadable } from '../utils/loadable';
import bbox from '@turf/bbox';
import React from 'react';
import type { Feature, FeatureCollection, MultiPolygon, Polygon } from 'geojson';
import { centroid, feature, featureCollection } from '@turf/turf';
import { GeoJsonLayer } from '@deck.gl/layers';
import { cameraFlightAtom } from '../map/flyToFeature';

/** Wider margin than the default fit: a region is a backdrop, not a feature to inspect. */
const REGION_FIT_PADDING = 80;

/**
 * Regions 9 and 10 reach across the antimeridian, so a fit to their bounds
 * spans the globe; they are flown to their centroid at this zoom instead.
 */
const ANTIMERIDIAN_REGION_ZOOM = 3;

/**
 * A FEMA region as this app draws it: the region number the picker and the
 * subscription form key by, plus whatever else the OpenFEMA row carries.
 */
export type FemaRegionProperties = { region: number } & Record<string, unknown>;

/** A drawn region: the regions are polygonal, the mainland ones single polygons. */
export type FemaRegionFeature = Feature<Polygon | MultiPolygon, FemaRegionProperties>;

export const FemaRegions = React.memo(() => {
  const [currentRegion, setCurrentRegion] = useAtom(currentRegionState);
  const flyTo = useSetAtom(cameraFlightAtom);
  const femaRegionsJson = useAtomValue(loadable(regionState));

  const handleRegionTransition = React.useCallback(
    (props: { region: FemaRegionFeature }) => {
      const { region } = props;
      if (region?.properties.region) {
        if ([9, 10].includes(region?.properties.region)) {
          const [longitude, latitude] = centroid(region).geometry.coordinates;
          flyTo({ center: [longitude, latitude], zoom: ANTIMERIDIAN_REGION_ZOOM });
        } else {
          flyTo({ bounds: bbox(region), padding: REGION_FIT_PADDING });
        }
      }
    },
    [flyTo]
  );

  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);

  const handleClick: React.MouseEventHandler<HTMLButtonElement> = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = React.useCallback(() => {
    setAnchorEl(null);
  }, [setAnchorEl]);

  const handleMenuClick = React.useCallback(
    (f: FemaRegionFeature | null) => {
      if (f == null) {
        handleClose();
        return setCurrentRegion(null);
      }

      if (f.properties.region !== currentRegion) {
        setCurrentRegion(f.properties.region);

        handleRegionTransition({ region: f });
      }
      handleClose();
    },
    [handleClose, handleRegionTransition, setCurrentRegion, currentRegion]
  );

  if (femaRegionsJson.state !== 'hasData') {
    return null;
  }

  return (
    <>
      <div className="region-picker">
        <Tooltip
          title="Zoom the map to a FEMA region and keep its outline on the map"
          describeChild
        >
          <Button
            variant="outlined"
            color="primary"
            aria-controls="simple-menu"
            aria-haspopup="true"
            onClick={handleClick}
            startIcon={<Language />}
            sx={{ color: 'white', backgroundColor: 'rgb(0, 82, 136, 1)' }}
            size="small"
          >
            {currentRegion ? `Region ${currentRegion}` : 'Region View'}
          </Button>
        </Tooltip>
        <Menu
          id="region-menu"
          anchorEl={anchorEl}
          keepMounted
          open={Boolean(anchorEl)}
          onClose={handleClose}
          slotProps={{
            paper: {
              style: {
                maxHeight: '300px',
                width: '20ch',
              },
            },
          }}
        >
          <MenuItem dense onClick={() => handleMenuClick(null)}>
            Clear
          </MenuItem>

          {femaRegionsJson?.data?.features
            ?.filter(f => f.properties?.region != null)
            ?.sort((a, b) => a.properties.region - b.properties.region)
            ?.map((f, index) => (
              <MenuItem dense key={index} onClick={() => handleMenuClick(f)}>
                Region: {f.properties.region}
              </MenuItem>
            ))}
        </Menu>
      </div>
    </>
  );
});

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

// The feed is unvalidated JSON, so the two things the map needs of a geometry --
// which of the polygonal shapes it is, and that it carries coordinates -- are
// checked before it is drawn.
const isPolygonal = (value: unknown): value is Polygon | MultiPolygon =>
  isRecord(value) &&
  (value.type === 'Polygon' || value.type === 'MultiPolygon') &&
  Array.isArray(value.coordinates);

export const regionState = atom(
  async (): Promise<FeatureCollection<Polygon | MultiPolygon, FemaRegionProperties>> => {
    try {
      // A plain GET: OpenFEMA returns `Access-Control-Allow-Origin: *`, but a
      // `Content-Type` request header forces a CORS preflight that the endpoint
      // answers with 503, blocking the fetch. No request body means no header is
      // needed.
      const response = await fetch('https://www.fema.gov/api/open/v2/FemaRegions');
      const json: unknown = await response.json();
      const rows = isRecord(json) && Array.isArray(json.FemaRegions) ? json.FemaRegions : [];
      return featureCollection(
        rows.filter(isRecord).flatMap((row): FemaRegionFeature[] => {
          const { regionGeometry, region, ...rest } = row;
          // A row without a region number is not one the picker, the layers or a
          // subscription can key by.
          if (typeof region !== 'number' || !isPolygonal(regionGeometry)) {
            return [];
          }
          return [
            feature<Polygon | MultiPolygon, FemaRegionProperties>(regionGeometry, {
              ...rest,
              region,
            }),
          ];
        })
      );
    } catch (e) {
      console.error(e);
      return featureCollection<Polygon | MultiPolygon, FemaRegionProperties>([]);
    }
  }
);

export const useRegionLayer = (props: { mode: 'all' | 'select' }) => {
  const femaRegionsJson = useAtomValue(loadable(regionState));
  const [currentRegion] = useAtom(currentRegionState);

  const regionLayer = React.useMemo(() => {
    if (femaRegionsJson.state !== 'hasData') {
      return [];
    }

    return [
      new GeoJsonLayer<FemaRegionProperties>({
        id: 'region-layer',
        data: femaRegionsJson.data,
        getFillColor: f => {
          if (props.mode === 'all') {
            return [0, 0, 0, 0];
          }

          return f.properties.region === currentRegion ? [0, 82, 135, 40] : [0, 0, 0, 0];
        },
        getLineColor: f => {
          if (props.mode === 'all') {
            return [0, 106, 165, 250];
          }
          return f.properties.region === currentRegion ? [0, 106, 165, 250] : [0, 0, 0, 0];
        },
        getLineWidth: f =>
          props.mode === 'all' ? 1 : f.properties.region === currentRegion ? 1 : 0,
        lineWidthMinPixels: 2,
        lineWidthScale: 2,
        pickable: false,
        stroked: props.mode === 'all' ? true : currentRegion != null,
        visible: props.mode === 'all' ? true : currentRegion != null,
        wrapLongitude: true,
        updateTriggers: {
          getFillColor: [props.mode, currentRegion, femaRegionsJson.data != null],
          getLineColor: [props.mode, currentRegion, femaRegionsJson.data != null],
          getLineWidth: [props.mode, currentRegion, femaRegionsJson.data != null],
        },
      }),
    ];
  }, [currentRegion, femaRegionsJson, props.mode]);

  return regionLayer;
};
